/**
 * Server-sync layer for the shared catalog slice of AppContext — products +
 * sales (TNK-82), live listings (TNK-83), and templates + store settings
 * (TNK-84). Mirrors inventorySync, but these are Manager+ surfaces, so the GET
 * helpers tolerate a 403 (a Staff session lacks catalog access) by returning []
 * instead of surfacing a sync error. The hydrate/migration in AppContext is
 * additionally gated on Manager+, so Staff never call these write endpoints.
 */
import { apiFetch } from "@/lib/apiClient";
import type {
  AppPreferences,
  LiveListing,
  ListingTemplate,
  PriceEntry,
  Product,
  RestockEvent,
  Sale,
  ShippingSupply,
} from "./AppContext";
import type { BundleTemplate } from "@/types/bundleTemplate";
import type { DeckTemplate } from "@/types/deckTemplate";
import type { BundleSettings } from "@/features/bundleEngine";
import { callApi, fireAndLog, type SyncOptions } from "./inventorySync";

export interface StoreSettings {
  bundleSettings?: BundleSettings;
  appPreferences?: AppPreferences;
}

// A caller without catalog access (Staff → 403) or before the feature exists
// (404) simply has no catalog — treat as empty rather than an error.
async function fetchListTolerant<T>(path: string): Promise<T[]> {
  const res = await apiFetch(path);
  if (res.status === 403 || res.status === 404) return [];
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return (await res.json()) as T[];
}

// ─── Products ──────────────────────────────────────────────────────────────
// TNK-102: default GET excludes archived rows; widen with ?include=archived
// for views that need both. The /products Archived tab uses only-archived.
export function fetchProducts(): Promise<Product[]> {
  return fetchListTolerant<Product>("/api/products?include=archived");
}
export function syncCreateProduct(p: Product, opts?: SyncOptions): void {
  fireAndLog(callApi("POST", "/api/products", p), "create product", opts);
}
export function syncUpdateProduct(p: Product, opts?: SyncOptions): void {
  fireAndLog(
    callApi("PATCH", `/api/products/${encodeURIComponent(p.id)}`, p),
    "update product",
    opts,
  );
}
export function syncDeleteProduct(id: string, opts?: SyncOptions): void {
  fireAndLog(
    callApi("DELETE", `/api/products/${encodeURIComponent(id)}`),
    "delete product",
    opts,
  );
}

/**
 * Soft-delete a product (TNK-102). Awaited so the caller can surface a 409
 * when Active listings still exist (server returns `{ error, activeListingIds }`)
 * — the operator should end those first. Returns the updated Product row
 * with archivedAt set.
 */
export async function archiveProductRequest(id: string): Promise<Product> {
  return (await callApi(
    "POST",
    `/api/products/${encodeURIComponent(id)}/archive`,
    {},
  )) as Product;
}
/** Clear archivedAt on a soft-deleted product. */
export async function restoreProductRequest(id: string): Promise<Product> {
  return (await callApi(
    "POST",
    `/api/products/${encodeURIComponent(id)}/restore`,
    {},
  )) as Product;
}

// ─── Sales ───────────────────────────────────────────────────────────────────
export function fetchSales(): Promise<Sale[]> {
  return fetchListTolerant<Sale>("/api/sales");
}
export function syncCreateSale(
  s: Sale,
  opts?: SyncOptions & { cardCount?: number },
): void {
  // cardCount is sent transient on the wire (server uses it to compute
  // suppliesBreakdown[].consumedQty before persisting); it's not stored on
  // the row itself, so the `Sale` type doesn't carry it.
  const payload =
    opts?.cardCount != null ? { ...s, cardCount: opts.cardCount } : s;
  fireAndLog(callApi("POST", "/api/sales", payload), "create sale", opts);
}

/** Awaited counterpart used by completeSale so the caller can read the
 *  server's first-sale-wins response (`supersededListings[]`) and render
 *  the supersede toast. Throws on non-2xx. */
export interface CreateSaleResponse extends Sale {
  supersededListings?: Array<{
    id: string;
    venue: string;
    ebayListingId?: string;
  }>;
}
export async function createSale(
  s: Sale,
  opts?: { cardCount?: number },
): Promise<CreateSaleResponse> {
  const payload =
    opts?.cardCount != null ? { ...s, cardCount: opts.cardCount } : s;
  return (await callApi("POST", "/api/sales", payload)) as CreateSaleResponse;
}
export function syncUpdateSale(s: Sale, opts?: SyncOptions): void {
  fireAndLog(
    callApi("PATCH", `/api/sales/${encodeURIComponent(s.id)}`, s),
    "update sale",
    opts,
  );
}
export function syncDeleteSale(id: string, opts?: SyncOptions): void {
  fireAndLog(
    callApi("DELETE", `/api/sales/${encodeURIComponent(id)}`),
    "delete sale",
    opts,
  );
}

/** Operator-initiated cancel (TNK-111). Awaited so the caller can read the
 *  server's restored sale row + surface 409s (already-cancelled, picked-
 *  without-restock) to the operator. */
export interface CancelSaleBody {
  reason: string;
  restock: boolean;
  /** eBay's defined cancel reason enum — only meaningful when the sale's
   *  venue is "eBay". Server passes through to the Post-Order Cancellation
   *  API; defaults to OUT_OF_STOCK_OR_CANNOT_FULFILL when omitted. */
  ebayCancelReason?:
    | "OUT_OF_STOCK_OR_CANNOT_FULFILL"
    | "ADDRESS_ISSUES"
    | "BUYER_ASKED_CANCEL"
    | "BUYER_DID_NOT_PAY"
    | "WONT_ARRIVE_ON_TIME";
}
export async function cancelSaleRequest(
  id: string,
  body: CancelSaleBody,
): Promise<Sale> {
  return (await callApi(
    "POST",
    `/api/sales/${encodeURIComponent(id)}/cancel`,
    body,
  )) as Sale;
}

/** Operator-initiated buyer refund (TNK-175). Independent of cancel; works on
 *  any not-yet-refunded sale, full or partial. Awaited so the caller can read
 *  the server's updated row + surface 409s (already-refunded). */
export interface RefundSaleBody {
  /** Refund amount in cents — full (sale total) or partial. */
  amountCents: number;
  reason: string;
}
export async function refundSaleRequest(
  id: string,
  body: RefundSaleBody,
): Promise<Sale> {
  return (await callApi(
    "POST",
    `/api/sales/${encodeURIComponent(id)}/refund`,
    body,
  )) as Sale;
}

// ─── Live listings ───────────────────────────────────────────────────────────
// Pull all statuses so the in-memory state covers Ended/Cancelled/Sold
// history needed by the multi-venue product detail and the /live filter tabs.
// Server defaults to ?status=Active for back-compat — we widen here.
export function fetchLiveListings(): Promise<LiveListing[]> {
  return fetchListTolerant<LiveListing>("/api/live-listings?status=All");
}
export function syncCreateLiveListing(
  l: LiveListing,
  opts?: SyncOptions,
): void {
  fireAndLog(callApi("POST", "/api/live-listings", l), "create listing", opts);
}
export function syncUpdateLiveListing(
  l: LiveListing,
  opts?: SyncOptions,
): void {
  fireAndLog(
    callApi("PATCH", `/api/live-listings/${encodeURIComponent(l.id)}`, l),
    "update listing",
    opts,
  );
}
export function syncDeleteLiveListing(id: string, opts?: SyncOptions): void {
  fireAndLog(
    callApi("DELETE", `/api/live-listings/${encodeURIComponent(id)}`),
    "delete listing",
    opts,
  );
}
// ─── Pick lists (TNK-102) ───────────────────────────────────────────────────
export interface ApiPickRow {
  id: string;
  saleId: string;
  productId: string;
  originalCardId: string;
  pickedCardId?: string;
  pickedAt?: string;
  notes: string;
  createdAt: string;
}

/** GET /api/sales/:saleId/picks — returns the current pick rows. Empty if
 *  init hasn't been called yet. */
export async function fetchPickList(saleId: string): Promise<ApiPickRow[]> {
  return (await callApi(
    "GET",
    `/api/sales/${encodeURIComponent(saleId)}/picks`,
  )) as ApiPickRow[];
}
/** POST /api/sales/:saleId/picks/init — idempotent. Snapshots the product's
 *  cardIds into pick_list rows at first call; subsequent calls return the
 *  existing rows unchanged. */
export async function initPickList(saleId: string): Promise<ApiPickRow[]> {
  return (await callApi(
    "POST",
    `/api/sales/${encodeURIComponent(saleId)}/picks/init`,
    {},
  )) as ApiPickRow[];
}
/** PATCH /api/picks/:id — mark picked, record substitution, or revert. */
export async function patchPickRow(
  id: string,
  body: { pickedCardId: string | null; notes?: string },
): Promise<ApiPickRow> {
  return (await callApi(
    "PATCH",
    `/api/picks/${encodeURIComponent(id)}`,
    body,
  )) as ApiPickRow;
}
/** POST /api/sales/:saleId/picks/complete — finalize. 409s with unfinished
 *  card ids if any row is still pending. */
export async function completePickList(
  saleId: string,
): Promise<{ saleId: string; pickedAt?: string }> {
  return (await callApi(
    "POST",
    `/api/sales/${encodeURIComponent(saleId)}/picks/complete`,
    {},
  )) as { saleId: string; pickedAt?: string };
}

/** End a listing (TNK-102). Sets status='Ended' server-side; optionally
 *  attempts an external platform end-push (eBay endItem) and surfaces the
 *  result via `externalEndedOk` on the returned row. Throws on non-2xx. */
export async function endLiveListingRequest(
  id: string,
  body: { reason: string; pushExternal: boolean },
): Promise<{ listing: LiveListing; externalError?: string }> {
  return (await callApi(
    "POST",
    `/api/live-listings/${encodeURIComponent(id)}/end`,
    body,
  )) as { listing: LiveListing; externalError?: string };
}

// ─── Listing templates ────────────────────────────────────────────────────────
export function fetchListingTemplates(): Promise<ListingTemplate[]> {
  return fetchListTolerant<ListingTemplate>("/api/listing-templates");
}
export function syncCreateListingTemplate(
  t: ListingTemplate,
  opts?: SyncOptions,
): void {
  fireAndLog(
    callApi("POST", "/api/listing-templates", t),
    "create listing template",
    opts,
  );
}
export function syncUpdateListingTemplate(
  t: ListingTemplate,
  opts?: SyncOptions,
): void {
  fireAndLog(
    callApi("PATCH", `/api/listing-templates/${encodeURIComponent(t.id)}`, t),
    "update listing template",
    opts,
  );
}
export function syncDeleteListingTemplate(
  id: string,
  opts?: SyncOptions,
): void {
  fireAndLog(
    callApi("DELETE", `/api/listing-templates/${encodeURIComponent(id)}`),
    "delete listing template",
    opts,
  );
}

// ─── Deck templates ────────────────────────────────────────────────────────────
export function fetchDeckTemplates(): Promise<DeckTemplate[]> {
  return fetchListTolerant<DeckTemplate>("/api/deck-templates");
}
export function syncCreateDeckTemplate(
  t: DeckTemplate,
  opts?: SyncOptions,
): void {
  fireAndLog(
    callApi("POST", "/api/deck-templates", t),
    "create deck template",
    opts,
  );
}
export function syncDeleteDeckTemplate(id: string, opts?: SyncOptions): void {
  fireAndLog(
    callApi("DELETE", `/api/deck-templates/${encodeURIComponent(id)}`),
    "delete deck template",
    opts,
  );
}

// ─── Bundle templates ──────────────────────────────────────────────────────────
export function fetchBundleTemplates(): Promise<BundleTemplate[]> {
  return fetchListTolerant<BundleTemplate>("/api/bundle-templates");
}
export function syncCreateBundleTemplate(
  t: BundleTemplate,
  opts?: SyncOptions,
): void {
  fireAndLog(
    callApi("POST", "/api/bundle-templates", t),
    "create bundle template",
    opts,
  );
}
export function syncUpdateBundleTemplate(
  t: BundleTemplate,
  opts?: SyncOptions,
): void {
  fireAndLog(
    callApi("PATCH", `/api/bundle-templates/${encodeURIComponent(t.id)}`, t),
    "update bundle template",
    opts,
  );
}
export function syncDeleteBundleTemplate(id: string, opts?: SyncOptions): void {
  fireAndLog(
    callApi("DELETE", `/api/bundle-templates/${encodeURIComponent(id)}`),
    "delete bundle template",
    opts,
  );
}

// ─── Store settings ────────────────────────────────────────────────────────────
// A single per-store row. GET returns {} when unset; PUT upserts.
export async function fetchStoreSettings(): Promise<StoreSettings> {
  const res = await apiFetch("/api/store-settings");
  if (res.status === 403 || res.status === 404) return {};
  if (!res.ok) throw new Error(`GET /api/store-settings → ${res.status}`);
  return (await res.json()) as StoreSettings;
}
export function syncStoreSettings(
  settings: StoreSettings,
  opts?: SyncOptions,
): void {
  fireAndLog(
    callApi("PUT", "/api/store-settings", settings),
    "save store settings",
    opts,
  );
}

/**
 * Awaited counterpart to syncStoreSettings — used by the supplies init wizard
 * so a page reload immediately after Save reliably sees the new tracking mode
 * instead of racing with a fire-and-forget PUT. Throws on non-2xx.
 */
export async function saveStoreSettings(
  settings: StoreSettings,
): Promise<void> {
  await callApi("PUT", "/api/store-settings", settings);
}

// ─── Shipping supplies ─────────────────────────────────────────────────────────
export function fetchShippingSupplies(): Promise<ShippingSupply[]> {
  return fetchListTolerant<ShippingSupply>("/api/shipping-supplies");
}
export function syncCreateShippingSupply(
  s: ShippingSupply,
  opts?: SyncOptions,
): void {
  fireAndLog(
    callApi("POST", "/api/shipping-supplies", s),
    "create shipping supply",
    opts,
  );
}

/** Awaited counterpart used by the supplies wizard so a follow-up restock POST
 *  doesn't race the supply-create POST and 404. Throws on non-2xx. */
export async function createShippingSupply(s: ShippingSupply): Promise<void> {
  await callApi("POST", "/api/shipping-supplies", s);
}
export function syncUpdateShippingSupply(
  s: ShippingSupply,
  opts?: SyncOptions,
): void {
  fireAndLog(
    callApi("PATCH", `/api/shipping-supplies/${encodeURIComponent(s.id)}`, s),
    "update shipping supply",
    opts,
  );
}
export function syncDeleteShippingSupply(id: string, opts?: SyncOptions): void {
  fireAndLog(
    callApi("DELETE", `/api/shipping-supplies/${encodeURIComponent(id)}`),
    "delete shipping supply",
    opts,
  );
}

/**
 * Restock endpoint (TNK-100). Unlike the sync* helpers above this is awaited
 * because the server is authoritative for the weighted-average math and the
 * caller needs the resulting supply state. Throws on non-2xx.
 */
export async function restockShippingSupply(
  id: string,
  batch: { addedQty: number; totalCost: number; supplier?: string },
): Promise<{ supply: ShippingSupply; event: RestockEvent }> {
  return (await callApi(
    "POST",
    `/api/shipping-supplies/${encodeURIComponent(id)}/restock`,
    batch,
  )) as { supply: ShippingSupply; event: RestockEvent };
}

// ─── Price entries ─────────────────────────────────────────────────────────────
export function fetchPriceEntries(): Promise<PriceEntry[]> {
  return fetchListTolerant<PriceEntry>("/api/price-entries");
}
export function syncCreatePriceEntry(e: PriceEntry, opts?: SyncOptions): void {
  fireAndLog(
    callApi("POST", "/api/price-entries", e),
    "create price entry",
    opts,
  );
}
export function syncDeletePriceEntry(id: string, opts?: SyncOptions): void {
  fireAndLog(
    callApi("DELETE", `/api/price-entries/${encodeURIComponent(id)}`),
    "delete price entry",
    opts,
  );
}

// ─── Migration helper ────────────────────────────────────────────────────────
/**
 * One-shot uploader for the operator's existing localStorage catalog, used on
 * first sign-in when the server catalog is empty. Chunked to stay under the
 * body cap. Awaits all writes so the caller can clear localStorage on success.
 */
export async function uploadCatalog(snapshot: {
  products: Product[];
  sales: Sale[];
  liveListings: LiveListing[];
  listingTemplates: ListingTemplate[];
  deckTemplates: DeckTemplate[];
  bundleTemplates: BundleTemplate[];
  shippingSupplies: ShippingSupply[];
  priceEntries: PriceEntry[];
  storeSettings?: StoreSettings;
}): Promise<void> {
  const CHUNK = 500;
  const bulk = async <T>(path: string, rows: T[]): Promise<void> => {
    for (let i = 0; i < rows.length; i += CHUNK) {
      await callApi("POST", path, rows.slice(i, i + CHUNK));
    }
  };
  await bulk("/api/products/bulk", snapshot.products);
  await bulk("/api/sales/bulk", snapshot.sales);
  await bulk("/api/live-listings/bulk", snapshot.liveListings);
  await bulk("/api/listing-templates/bulk", snapshot.listingTemplates);
  await bulk("/api/deck-templates/bulk", snapshot.deckTemplates);
  await bulk("/api/bundle-templates/bulk", snapshot.bundleTemplates);
  await bulk("/api/shipping-supplies/bulk", snapshot.shippingSupplies);
  await bulk("/api/price-entries/bulk", snapshot.priceEntries);
  if (snapshot.storeSettings) {
    await callApi("PUT", "/api/store-settings", snapshot.storeSettings);
  }
}
